<!--
  BandTable — tabella di fasce km editabili.

  Vocabolario unico per tutti i listini a fasce: pickup, transfer, notturno e
  fleet (che distingue 1 e 2 operatori). Il valet ha una tabella sua
  (ValetBandTable): si prezza a pacchetti.

  v-model: array di righe
    { min_km, max_km, mode, price, price_per_km?,
      price2?, price2_per_km?, price_share?, price_share_per_km? }

  - `mode` è il MODO DI CALCOLO DELLA FASCIA: «A fascia» (prezzo
    della corsa), «Al km» (€ per ogni km) o «Fisso + €/km» oltre la soglia. Il
    backend applica a ogni corsa il modo della fascia in cui cade, quindi un
    listino può mescolarli: 0–10 km a fascia, 10–20 km al km.
  - `*_per_km` è il €/km oltre la soglia, solo nei modi «Fisso + €/km». Ogni
    colonna ha il suo: a database 1 operatore, 2 operatori e quota partner sono
    righe distinte, ognuna con il proprio `price_per_km`. Il €/km dei 2
    operatori e della quota, se vuoto, vale quello della prima colonna (il
    segnaposto lo mostra).
  - `max_km` a null significa "da qui in poi": è l'ultima fascia, senza limite.
  - `default-mode`: il modo della prima fascia; le successive copiano quello
    della fascia precedente.
  - `two-operators` aggiunge la colonna del secondo operatore (`price2`).
  - `with-share` aggiunge la QUOTA PARTNER (`price_share`): a database sono
    righe separate con `is_partner_share = true`, ma le fasce km e il modo sono
    gli stessi, quindi chiederli due volte sarebbe solo un modo per farli
    divergere. Con `two-operators` la quota è doppia (`price_share2`): il
    preventivo del partner la cerca col numero di operatori della corsa.

  L'unità sta accanto a OGNI importo: con modi diversi nello stesso listino,
  "3" in testata non direbbe se sono 3 € per la corsa o 3 € al km.
-->
<template>
  <div class="bt">
    <table class="bt__tab">
      <thead>
        <tr>
          <th>Fascia km</th>
          <th>Come si calcola</th>
          <th
            v-for="c in columns"
            :key="c.key"
            class="bt__num"
          >
            {{ c.header }}
          </th>
          <th class="bt__act" />
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(b, i) in rows"
          :key="i"
        >
          <td>
            <div class="bt__kmpair">
              <el-input
                v-model.number="b.min_km"
                size="small"
                type="number"
                min="0"
                class="bt__km"
                @change="emitRows"
              />
              <span class="bt__sep">–</span>
              <el-input
                v-if="b.max_km !== null"
                v-model.number="b.max_km"
                size="small"
                type="number"
                min="0"
                class="bt__km"
                @change="emitRows"
              />
              <span
                v-else
                class="bt__open"
              >oltre</span>
              <span class="bt__unit">km</span>
            </div>
          </td>

          <td>
            <el-select
              :model-value="modeOf(b)"
              size="small"
              class="bt__mode"
              :aria-label="`Come si calcola la fascia ${i + 1}`"
              @update:model-value="(v) => setMode(b, v)"
            >
              <el-option
                v-for="m in modesFor(b)"
                :key="m.value"
                :label="`${m.label} · ${m.hint}`"
                :value="m.value"
              />
            </el-select>
            <!-- la soglia vale per tutte le colonne: si dice una volta sola -->
            <div
              v-if="isExcessMode(modeOf(b))"
              class="bt__threshold"
            >
              {{ excessLabel(b) }}
            </div>
          </td>

          <td
            v-for="c in columns"
            :key="c.key"
            class="bt__num"
          >
            <div class="bt__amount">
              <el-input
                v-model.number="b[c.key]"
                size="small"
                type="number"
                step="0.01"
                class="bt__price"
                :placeholder="c.fallback ? placeholderOf(b[c.fallback]) : ''"
                :label="`${c.header}, fascia ${i + 1}`"
                @change="emitRows"
              />
              <span class="bt__unit">{{ unitOf(b) }}</span>
            </div>
            <!-- parte chilometrica: solo nei modi «Fisso + €/km» -->
            <div
              v-if="isExcessMode(modeOf(b))"
              class="bt__excess"
            >
              <span class="bt__plus">+</span>
              <el-input
                v-model.number="b[c.perKm]"
                size="small"
                type="number"
                step="0.01"
                class="bt__km"
                :placeholder="perKmPlaceholder(b, c)"
                :label="`€/km ${c.header}, fascia ${i + 1}`"
                @change="emitRows"
              />
              <span class="bt__unit">€/km</span>
            </div>
          </td>

          <td class="bt__act">
            <el-button
              type="danger"
              plain
              size="small"
              :aria-label="`Togli la fascia ${i + 1}`"
              @click="removeRow(i)"
            >
              ×
            </el-button>
          </td>
        </tr>

        <tr v-if="!rows.length">
          <td
            :colspan="3 + columns.length"
            class="bt__empty"
          >
            Nessuna fascia impostata.
          </td>
        </tr>
      </tbody>
    </table>

    <div class="bt__actions">
      <el-button
        size="small"
        @click="addRow(false)"
      >
        ＋ Aggiungi fascia
      </el-button>
      <el-button
        v-if="!hasOpenBand"
        size="small"
        @click="addRow(true)"
      >
        ＋ Fascia senza limite
      </el-button>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';
import { MODES, isExcessMode, shortUnitForMode } from '../../services/tariffModel.js';

// Campi €/km della riga, uno per colonna di prezzo.
const PER_KM_FIELDS = ['price_per_km', 'price2_per_km', 'price_share_per_km', 'price_share2_per_km'];

const blank = (v) => v === null || v === undefined || v === '';

export default {
  name: 'BandTable',

  props: {
    modelValue: { type: Array, default: () => [] },
    twoOperators: { type: Boolean, default: false },
    withShare: { type: Boolean, default: false },
    // Modo della prima fascia (quello del servizio): le altre copiano la
    // fascia precedente.
    defaultMode: { type: String, default: 'flat' },
    // Etichetta facoltativa della colonna principale.
    priceLabel: { type: String, default: '' },
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const rows = computed(() => props.modelValue);

    // Le colonne di prezzo, ognuna con il suo campo €/km. `fallback` è il
    // campo che il salvataggio usa se il prezzo resta vuoto, `perKmFallback`
    // quelli (in ordine) per il €/km vuoto: vanno tenuti allineati al
    // salvataggio in DetailPartnerPage e AddPartnerPage.
    const columns = computed(() => [
      {
        key: 'price',
        perKm: 'price_per_km',
        header: props.twoOperators ? 'Prezzo · a catena' : props.priceLabel || 'Prezzo',
        perKmFallback: [],
      },
      ...(props.twoOperators
        ? [{
          key: 'price2',
          perKm: 'price2_per_km',
          header: 'Prezzo · singolo',
          fallback: 'price',
          perKmFallback: ['price_per_km'],
        }]
        : []),
      ...(props.withShare
        ? [{
          key: 'price_share',
          perKm: 'price_share_per_km',
          header: props.twoOperators ? 'Quota partner · a catena' : 'Quota partner',
          perKmFallback: ['price_per_km'],
        }]
        : []),
      ...(props.withShare && props.twoOperators
        ? [{
          key: 'price_share2',
          perKm: 'price_share2_per_km',
          header: 'Quota partner · singolo',
          perKmFallback: ['price_share_per_km', 'price_per_km'],
        }]
        : []),
    ]);

    // Una sola fascia può restare aperta: due "da qui in poi" si
    // sovrapporrebbero e il backend ne risolverebbe una a caso.
    const hasOpenBand = computed(() => rows.value.some((b) => b.max_km === null));

    const modeOf = (b) => b.mode || props.defaultMode;
    const unitOf = (b) => shortUnitForMode(modeOf(b));
    const excessLabel = (b) =>
      modeOf(b) === 'flat_plus_km_total'
        ? '€/km su tutti i km'
        : `€/km oltre ${Number(b.min_km || 0).toLocaleString('it-IT')} km`;
    const placeholderOf = (v) => (blank(v) ? '' : String(v));
    // il €/km che vale se la colonna resta vuota: il primo compilato
    const perKmPlaceholder = (b, c) =>
      placeholderOf(c.perKmFallback.map((f) => b[f]).find((v) => !blank(v)));

    const modesFor = () => MODES;

    const emitRows = () => emit('update:modelValue', [...rows.value]);

    const setMode = (b, mode) => {
      b.mode = mode;
      // senza parte chilometrica i €/km scritti prima non devono restare
      // nascosti nella riga e finire salvati
      if (!isExcessMode(mode)) PER_KM_FIELDS.forEach((f) => { b[f] = null; });
      emitRows();
    };

    const addRow = (open) => {
      // la fascia nuova riparte da dove finisce l'ultima chiusa, con il modo
      // dell'ultima fascia
      const closed = rows.value.filter((b) => b.max_km !== null);
      const last = closed[closed.length - 1];
      const previous = rows.value[rows.value.length - 1];
      const from = last ? Number(last.max_km) : 0;
      const next = {
        min_km: from,
        max_km: open ? null : from + 10,
        mode: previous ? modeOf(previous) : props.defaultMode,
        price: null,
        price_per_km: null,
      };
      if (props.twoOperators) Object.assign(next, { price2: null, price2_per_km: null });
      if (props.withShare) Object.assign(next, { price_share: null, price_share_per_km: null });
      if (props.withShare && props.twoOperators) {
        Object.assign(next, { price_share2: null, price_share2_per_km: null });
      }
      emit('update:modelValue', [...rows.value, next]);
    };

    const removeRow = (i) => {
      const next = [...rows.value];
      next.splice(i, 1);
      emit('update:modelValue', next);
    };

    return {
      rows, columns, hasOpenBand, modeOf, unitOf, excessLabel, placeholderOf, perKmPlaceholder, modesFor,
      isExcessMode, emitRows, setMode, addRow, removeRow,
    };
  },
};
</script>

<style scoped>
/* Con 1 e 2 operatori, quota partner e €/km la tabella supera la larghezza
   di un dialogo stretto: scorre dentro di sé, non allarga la pagina. */
.bt { overflow-x: auto; }
.bt__tab { width: 100%; border-collapse: collapse; }
.bt__tab th {
  text-align: left; font-size: 10.5px; text-transform: uppercase;
  letter-spacing: 0.04em; color: var(--text-tertiary); font-weight: 700;
  padding: 6px 8px; white-space: nowrap;
}
.bt__tab td { padding: 5px 8px; vertical-align: top; }
.bt__tab th.bt__num, .bt__num { text-align: right; }
/* «Quota partner · singolo» è più largo dei campi sotto: va a capo
   invece di allargare la tabella */
.bt__tab th.bt__num { white-space: normal; text-wrap: balance; min-width: 96px; }
.bt__act { width: 48px; text-align: right; }

.bt__kmpair { display: flex; align-items: center; gap: 6px; }
/* larghezze strette: con 1 e 2 operatori e quota partner, ognuno col suo €/km,
   la tabella deve stare in un dialogo senza scorrere */
.bt__km { width: 68px; }
.bt__mode { width: 196px; }
.bt__amount { display: flex; align-items: center; justify-content: flex-end; gap: 6px; }
.bt__price { width: 88px; }
.bt__sep { color: var(--text-tertiary); }
.bt__open {
  font-size: 12px; color: var(--text-tertiary); font-style: italic;
  min-width: 44px;
}
.bt__unit { font-size: 11.5px; color: var(--text-tertiary); white-space: nowrap; }

.bt__threshold {
  font-size: 11.5px; color: var(--text-tertiary); white-space: nowrap;
  /* allineata alla riga dei €/km nelle colonne di prezzo */
  margin-top: 5px; line-height: 24px;
}
.bt__excess {
  display: flex; align-items: center; justify-content: flex-end;
  gap: 6px; margin-top: 5px;
}
.bt__plus { color: var(--text-tertiary); }

.bt__empty {
  font-size: 12.5px; color: var(--text-tertiary); padding: 10px 8px !important;
}
.bt__actions { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 10px; }
</style>
